import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "react-toastify";
import { describeApiError } from "../../../api/client";
import "../OpsShared.css";
import "../../Team/people.css";
import "../Sops/sops.css";
import "./documents.css";
import {
  getOpsLibrary, adoptStarter, listOpsDocs, getOpsDoc, createOpsDoc, updateOpsDoc, uploadOpsFile,
  generateStarters, confirmOpsSection, confirmOpsDoc, listOpsQuestions,
} from "../../../api/opsDocsApi";
import { getUserRole } from "../../../helpers/tenantHelpers";
import QuestionQueue from "./QuestionQueue";

/* Operations → Documents: contracts, waivers, checklists, rules and policies.
   - Your documents (written here in markdown, or your own uploaded file).
   - The library: what your industry's pack offers (events, landscaping, …). Adding
     one copies it in; a starter SOP lands as a draft in Operations → SOPs.
   - {{merge.fields}} are filled from the booking when a document is attached to a
     quote (Quote → Documents); anything the booking doesn't know stays visible.
   - Generate starter documents: the whole pack as drafts, with every question the
     templates need in one queue. One answer fills every document that uses it and the
     business's knowledge (what the assistant / receptionist / quotes read).
   - Status: draft → needs answers → ready for review → confirmed. Counsel-flagged
     sections stay "needs counsel" until a person confirms each; only a confirmed
     document goes with a booking. */

const AUTHOR_ROLES = new Set(["owner", "admin", "manager"]);
const KINDS = [["contract", "Contract"], ["waiver", "Waiver"], ["checklist", "Checklist"], ["rules", "Rules"],
  ["policy", "Policy"], ["safety", "Safety"], ["other", "Other"]];
const kindLabel = (k) => (KINDS.find(([v]) => v === k) || [k, "Other"])[1];
const STEPS = [["needs_answers", "Needs answers"], ["ready_for_review", "Ready for review"], ["confirmed", "Confirmed"]];
const statusLabel = (st) => ({ draft: "Draft", needs_answers: "Needs answers", ready_for_review: "Ready for review",
  confirmed: "Confirmed", archived: "Archived" }[st] || st);
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

/* Merge fields render as chips so "this gets filled from the booking" is visible. */
function withFieldChips(body) {
  return String(body || "").replace(/\{\{\s*([a-z_]+\.[a-z_]+|today)\s*\}\}/g, "`{{$1}}`");
}

export default function DocumentsPage() {
  const role = String(getUserRole("Member") || "").toLowerCase();
  const canAuthor = AUTHOR_ROLES.has(role);
  const location = useLocation();
  const navigate = useNavigate();

  const [docs, setDocs] = useState([]);
  const [library, setLibrary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(() => new URLSearchParams(location.search).get("open"));
  const [doc, setDoc] = useState(null);
  const [preview, setPreview] = useState(null);        // a library starter being previewed
  const [editing, setEditing] = useState(null);        // {mode:'new'|'edit', title, kind, body}
  const [busy, setBusy] = useState(false);
  const [showQueue, setShowQueue] = useState(false);
  const [openQ, setOpenQ] = useState(0);
  // platform owner only: preview another vertical's pack (the internal tenant has no industry of its own)
  const [pack, setPack] = useState(() => new URLSearchParams(location.search).get("pack") || "");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, l, q] = await Promise.all([listOpsDocs(), getOpsLibrary(pack || undefined), listOpsQuestions(true)]);
      setDocs(d.data.documents || []);
      setLibrary(l.data);
      setOpenQ(q.data.open || 0);
      setError(null);
    } catch (e) {
      setError(describeApiError(e, "Couldn't load documents"));
    } finally {
      setLoading(false);
    }
  }, [pack]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!openId) { setDoc(null); return; }
    setPreview(null);
    getOpsDoc(openId).then((r) => setDoc(r.data)).catch((e) => toast.error(describeApiError(e, "Couldn't open the document")));
    navigate({ search: `?open=${openId}${pack ? `&pack=${pack}` : ""}` }, { replace: true });
  }, [openId, navigate, pack]);

  const reloadDoc = async (id = openId) => {
    if (!id) return;
    try { setDoc((await getOpsDoc(id)).data); } catch { /* list reload shows the state */ }
  };

  const grouped = useMemo(() => {
    const g = {};
    for (const d of docs) (g[d.kind] = g[d.kind] || []).push(d);
    return KINDS.filter(([k]) => g[k]).map(([k, label]) => [label, g[k]]);
  }, [docs]);

  const adopt = async (item) => {
    setBusy(true);
    try {
      const r = await adoptStarter(item.key, pack || undefined);
      if (r.data.type === "sop") {
        toast.success(`"${r.data.sop.title}" added to SOPs as a draft`);
        await load();
        navigate(`/operations/sops?open=${encodeURIComponent(r.data.sop.slug)}`);
      } else {
        await load();
        setOpenId(r.data.document.id);
        toast.success(`"${r.data.document.title}" added to your documents`);
      }
    } catch (e) {
      toast.error(describeApiError(e, "Couldn't add it"));
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!editing.title.trim()) { toast.error("Give it a title"); return; }
    setBusy(true);
    try {
      const body = { title: editing.title, kind: editing.kind, body: editing.body };
      if (editing.file_id) body.file_id = editing.file_id;
      const r = editing.mode === "new" ? await createOpsDoc(body) : await updateOpsDoc(doc.id, body);
      setEditing(null);
      await load();
      setOpenId(r.data.id);
      setDoc(r.data);
    } catch (e) {
      toast.error(describeApiError(e, "Couldn't save"));
    } finally {
      setBusy(false);
    }
  };

  const run = async (fn, fail) => {
    setBusy(true);
    try {
      const r = await fn();
      if (r?.data?.id) setDoc(r.data);
      await load();
      return r;
    } catch (e) {
      toast.error(describeApiError(e, fail));
      return null;
    } finally {
      setBusy(false);
    }
  };
  const setStatus = (status) => run(() => updateOpsDoc(doc.id, { status }), "Couldn't change the status");
  const confirmSection = (sid) => run(() => confirmOpsSection(doc.id, sid), "Couldn't confirm that section");
  const confirmDoc = () => run(() => confirmOpsDoc(doc.id), "Couldn't confirm");
  const generate = async () => {
    const r = await run(() => generateStarters(pack || undefined), "Couldn't generate the starter documents");
    if (r) {
      const o = r.data;
      toast.success(`${o.documents} documents and ${o.sops} SOPs ready as drafts${o.questions_open ? ` · ${o.questions_open} questions to answer` : ""}`);
      setOpenId(null); setPreview(null); setEditing(null); setShowQueue(true);
    }
  };

  const onUpload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const r = await uploadOpsFile(file);
      setEditing((ed) => ({ ...ed, file_id: r.data.id, file_name: r.data.name || file.name,
        title: ed.title || file.name.replace(/\.[^.]+$/, "") }));
    } catch (e) {
      toast.error(describeApiError(e, "Upload failed"));
    } finally {
      setBusy(false);
    }
  };

  const libDocs = library?.documents || [];
  const libSops = library?.sops || [];

  return (
    <div className="ops-page">
      <header className="ops-head">
        <div>
          <h1>Documents</h1>
          <p className="ops-muted">Contracts, waivers, checklists and rules. Attach one to a quote and it travels with that booking, filled in with the client and event details.</p>
        </div>
        <div className="ops-actions">
          {library?.packs_available ? (
            <select className="ops-btn secondary" aria-label="Preview a library" value={pack} onChange={(e) => { setPack(e.target.value); setPreview(null); }}>
              <option value="">{library?.industry && !pack ? `Library: own industry` : "Library: own industry"}</option>
              {library.packs_available.map((p) => <option key={p} value={p}>Preview: {p}</option>)}
            </select>
          ) : null}
          {canAuthor && library?.pack && <button className="ops-btn" disabled={busy} onClick={generate}>Generate starter documents</button>}
          {canAuthor && <button className="ops-btn secondary" onClick={() => { setOpenId(null); setPreview(null); setShowQueue(false); setEditing({ mode: "new", title: "", kind: "contract", body: "" }); }}>New document</button>}
        </div>
      </header>

      {error ? <div className="ppl-note danger">{error}</div> : null}

      <div className="sop-layout">
        <aside className="ppl-card sop-library">
          {loading ? <div className="ppl-muted">Loading…</div> : (
            <>
              {openQ > 0 || showQueue ? (
                <button className={`sop-row ${showQueue ? "is-open" : ""}`} onClick={() => { setShowQueue(true); setOpenId(null); setPreview(null); setEditing(null); }}>
                  <span className="sop-row-main"><span className="ppl-name">Questions to answer</span><span className="ppl-muted">{openQ} waiting</span></span>
                </button>
              ) : null}
              <div className="sop-group-title">Your documents</div>
              {grouped.length === 0 ? <div className="ppl-muted doc-empty">None yet. Add one from the library below, write your own, or upload the file you use today.</div>
                : grouped.map(([label, rows]) => (
                  <div className="sop-group" key={label}>
                    <div className="sop-group-title">{label}</div>
                    {rows.map((d) => (
                      <button key={d.id} className={`sop-row ${openId === d.id ? "is-open" : ""}`} onClick={() => { setEditing(null); setShowQueue(false); setOpenId(d.id); }}>
                        <span className="sop-row-main">
                          <span className="ppl-name">{d.title}</span>
                          <span className="ppl-muted">{statusLabel(d.status)}{d.counsel_pending?.length && d.status !== "confirmed" ? " · needs counsel" : ""}{d.file ? " · file" : ""}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                ))}

              <div className="sop-group doc-library">
                <div className="sop-group-title">Library{library?.pack ? ` · ${library.pack}` : ""}</div>
                {!library?.pack ? <div className="ppl-muted doc-empty">No starter library for this business's industry yet. Your own documents work the same way.</div> : (
                  <>
                    {libDocs.map((it) => (
                      <button key={it.key} className={`sop-row ${preview?.key === it.key ? "is-open" : ""}`} onClick={() => { setEditing(null); setShowQueue(false); setOpenId(null); setPreview({ ...it, type: "document" }); }}>
                        <span className="sop-row-main">
                          <span className="ppl-name">{it.title}</span>
                          <span className="ppl-muted">{kindLabel(it.kind)}{it.adopted_id ? " · added" : ""}{it.needs_counsel ? " · counsel review" : ""}</span>
                        </span>
                      </button>
                    ))}
                    <div className="sop-group-title doc-sub">Starter SOPs</div>
                    {libSops.map((it) => (
                      <button key={it.key} className={`sop-row ${preview?.key === it.key ? "is-open" : ""}`} onClick={() => { setEditing(null); setShowQueue(false); setOpenId(null); setPreview({ ...it, type: "sop" }); }}>
                        <span className="sop-row-main">
                          <span className="ppl-name">{it.title}</span>
                          <span className="ppl-muted">SOP{it.adopted_slug ? " · added" : ""}</span>
                        </span>
                      </button>
                    ))}
                  </>
                )}
              </div>
            </>
          )}
        </aside>

        <main className="sop-doc">
          {showQueue && !editing ? (
            <>
              <div className="sop-doc-head"><div><h2>Questions to answer</h2>
                <div className="sop-doc-meta">Each answer goes into every document that uses it, and into what the assistant knows about the business.</div></div></div>
              <QuestionQueue onAnswered={() => load()} />
            </>
          ) : editing ? (
            <div className="doc-editor">
              <h2>{editing.mode === "new" ? "New document" : "Edit document"}</h2>
              <label className="doc-field"><span>Title</span>
                <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
              </label>
              <label className="doc-field"><span>Kind</span>
                <select value={editing.kind} onChange={(e) => setEditing({ ...editing, kind: e.target.value })}>
                  {KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </label>
              <label className="doc-field"><span>Text (markdown; use {"{{client.name}}"}, {"{{event.date}}"} … for details filled from the booking)</span>
                <textarea rows={18} value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} />
              </label>
              <div className="doc-field"><span>Or your own file</span>
                <div className="doc-upload">
                  <input type="file" accept=".pdf,.doc,.docx,.txt" onChange={(e) => onUpload(e.target.files?.[0])} disabled={busy} />
                  {editing.file_name ? <span className="ppl-muted">Attached: {editing.file_name}</span> : null}
                </div>
              </div>
              <div className="sop-toolbar">
                <button className="ops-btn" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save"}</button>
                <button className="ops-btn secondary" onClick={() => setEditing(null)} disabled={busy}>Cancel</button>
              </div>
            </div>
          ) : preview ? (
            <>
              <div className="sop-doc-head">
                <div>
                  <h2>{preview.title}</h2>
                  <div className="sop-doc-meta">Library · {preview.type === "sop" ? "Starter SOP" : kindLabel(preview.kind)}</div>
                </div>
              </div>
              {preview.needs_counsel ? <CounselBanner /> : null}
              <div className="sop-toolbar">
                {preview.type === "document" && preview.adopted_id ? <button className="ops-btn secondary" onClick={() => setOpenId(preview.adopted_id)}>Open your copy</button>
                  : preview.type === "sop" && preview.adopted_slug ? <Link className="ops-btn secondary" to={`/operations/sops?open=${encodeURIComponent(preview.adopted_slug)}`}>Open in SOPs</Link>
                    : canAuthor ? <button className="ops-btn" disabled={busy} onClick={() => adopt(preview)}>{preview.type === "sop" ? "Add as a draft SOP" : "Add to my documents"}</button>
                      : <span className="ppl-muted">A manager can add this.</span>}
              </div>
              <div className="sop-md doc-preview">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{withFieldChips(preview.preview) + "\n\n…"}</ReactMarkdown>
              </div>
            </>
          ) : doc ? (
            <>
              <div className="sop-doc-head">
                <div>
                  <h2>{doc.title}</h2>
                  <div className="sop-doc-meta">{kindLabel(doc.kind)} · updated {fmtDate(doc.updated_at)}{doc.starter_key ? " · from the library" : ""}</div>
                </div>
              </div>
              {doc.status === "archived" ? (
                <div className="sop-banner is-draft"><strong>Archived</strong><span className="sop-banner-text">Not offered when attaching to bookings.</span></div>
              ) : (
                <div className="doc-status" aria-label="Status">
                  {STEPS.map(([k, label]) => {
                    const order = STEPS.findIndex(([x]) => x === doc.status);
                    const i = STEPS.findIndex(([x]) => x === k);
                    return <span key={k} className={`doc-step ${k === doc.status ? "is-now" : order > i ? "is-done" : ""}`}>{label}</span>;
                  })}
                </div>
              )}
              {doc.counsel_pending?.length && doc.status !== "confirmed" ? <CounselBanner /> : null}
              {doc.sections?.some((x) => x.counsel_flag) ? (
                <>
                  <div className="doc-section-title">Counsel review</div>
                  <div className="doc-counsel-list">
                    {doc.sections.filter((x) => x.counsel_flag).map((x) => (
                      <div key={x.id} className={`doc-counsel-row ${x.confirmed ? "is-done" : ""}`}>
                        <span><b>{x.heading}</b>{x.confirmed ? ` · confirmed by ${x.confirmed.by}` : ` · ${x.counsel_note || "needs counsel review"}`}</span>
                        {!x.confirmed && canAuthor ? (
                          <button className="bkdoc-btn" disabled={busy} title="Only after counsel has reviewed this section's wording"
                            onClick={() => confirmSection(x.id)}>Counsel has reviewed this</button>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </>
              ) : null}
              {canAuthor && doc.status !== "archived" ? (
                <div className="sop-toolbar">
                  {!doc.structured && <button className="ops-btn secondary" onClick={() => setEditing({ mode: "edit", title: doc.title, kind: doc.kind, body: doc.body || "", file_name: doc.file?.name })}>Edit</button>}
                  {doc.status === "ready_for_review" && <button className="ops-btn secondary" disabled={busy || doc.counsel_pending?.length > 0}
                    title={doc.counsel_pending?.length ? "Counsel review is still open on this document" : ""} onClick={confirmDoc}>Confirm document</button>}
                  <button className="ops-btn ghost" disabled={busy} onClick={() => setStatus("archived")}>Archive</button>
                </div>
              ) : canAuthor && doc.status === "archived" ? (
                <div className="sop-toolbar"><button className="ops-btn secondary" disabled={busy} onClick={() => setStatus("active")}>Restore</button></div>
              ) : null}
              {doc.questions?.length ? (
                <>
                  <div className="doc-section-title">This document's questions</div>
                  <QuestionQueue keys={doc.questions.map((q) => q.key)} onAnswered={() => { reloadDoc(doc.id); load(); }} />
                  <div className="doc-section-title">The document</div>
                </>
              ) : null}
              {doc.merge_fields?.length ? (
                <div className="doc-fields"><span className="ppl-muted">Filled from the booking:</span>{doc.merge_fields.map((f) => <code key={f}>{f}</code>)}</div>
              ) : null}
              {doc.file ? <div className="doc-file">File: {doc.file.url ? <a href={doc.file.url} target="_blank" rel="noreferrer">{doc.file.name || "open"}</a> : doc.file.name}</div> : null}
              {doc.body ? <div className="sop-md"><ReactMarkdown remarkPlugins={[remarkGfm]}>{withFieldChips(doc.body)}</ReactMarkdown></div> : null}
            </>
          ) : (
            <div className="ppl-empty">Open a document, or add one from the library.</div>
          )}
        </main>
      </div>
    </div>
  );
}

function CounselBanner() {
  return (
    <div className="sop-banner is-draft doc-counsel">
      <strong>Counsel review</strong>
      <span className="sop-banner-text">A starting draft, not legal advice. Every section marked for counsel must be reviewed by a lawyer, and confirmed here by a person, before this goes to a client.</span>
    </div>
  );
}
